import React from 'react';
import { Box, Typography } from '@mui/material';
import { Star as StarIcon } from '@mui/icons-material';
import { colors } from '../../../theme/designTokens';
import { MoodOrderItem } from './types';
import GridDropSlot from '../styles/GridDropSlot';
import { MAX_ACTIVE_MOODS } from './constants';

interface AppMoodGridPanelStats {
  inGrid: number;
  total: number;
  inactive: number;
  totalUsage: number;
}

interface AppMoodGridPanelProps {
  activeItems: MoodOrderItem[];
  stats: AppMoodGridPanelStats;
}

const AppMoodGridPanel: React.FC<AppMoodGridPanelProps> = ({
  activeItems,
  stats,
}) => {
  // V5.5 2026-08-09: 5 slots (Neutral default + 4 emotional moods)
  const top = activeItems.slice(0, MAX_ACTIVE_MOODS);
  const slots: (MoodOrderItem | null)[] = Array.from(
    { length: MAX_ACTIVE_MOODS },
    (_, i) => top[i] || null,
  );

  return (
    <Box
      sx={{
        bgcolor: '#0d3d50',
        width: 290,
        flexShrink: 0,
        overflowY: 'auto',
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2.5,
        borderLeft: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontFamily: '"Playfair Display", serif',
          fontStyle: 'italic',
          fontSize: '1.06rem',
          color: '#fff',
          fontWeight: 700,
          letterSpacing: '0.01em',
        }}
      >
        App Mood Grid
      </Typography>

      {/* 2x2 grid of drop slots */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '8px',
        }}
      >
        {slots.map((item, index) => (
          <GridDropSlot
            key={index}
            index={index}
            item={item}
            isMostPopular={index === 0}
          />
        ))}
      </Box>

      {/* Drag hint */}
      <Typography
        sx={{
          fontFamily: '"Montserrat", sans-serif',
          fontSize: '0.72rem',
          color: 'rgba(255,255,255,0.55)',
          textAlign: 'center',
          lineHeight: 1.5,
        }}
      >
        Drag cards from the left panel into slots to reorder the mood grid
      </Typography>

      {/* Stats mini grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '6px',
        }}
      >
        {[
          { label: 'In Grid', value: stats.inGrid, color: colors.warmGold[500] },
          { label: 'Total', value: stats.total, color: '#fff' },
          { label: 'Inactive', value: stats.inactive, color: '#ef4444' },
          { label: 'Total Usage', value: stats.totalUsage.toLocaleString(), color: '#4ade80' },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              bgcolor: 'rgba(255,255,255,0.04)',
              borderRadius: '6px',
              p: 1,
              textAlign: 'center',
            }}
          >
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontWeight: 700,
                fontSize: '1.2rem',
                color: stat.color,
              }}
            >
              {stat.value}
            </Typography>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.66rem',
                color: 'rgba(255,255,255,0.65)',
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Mini phone preview */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box
          sx={{
            width: 140,
            bgcolor: '#0a2e3d',
            borderRadius: '14px',
            p: 1,
            border: '2px solid rgba(255,255,255,0.08)',
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 4,
              bgcolor: 'rgba(255,255,255,0.1)',
              borderRadius: 2,
              mx: 'auto',
              mb: 1,
            }}
          />
          <Box sx={{ bgcolor: '#f5f0e8', borderRadius: '8px', p: 0.8 }}>
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.4rem',
                fontWeight: 700,
                color: '#1e293b',
                textAlign: 'center',
                mb: 0.6,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
              }}
            >
              Choose Mood
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3px' }}>
              {slots.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    bgcolor: item ? '#fff' : '#e5e7eb',
                    borderRadius: '4px',
                    p: 0.4,
                    border: item
                      ? i === 0
                        ? `1px solid ${colors.warmGold[500]}`
                        : '1px solid #e2e8f0'
                      : '1px dashed #d1d5db',
                    minHeight: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {item ? (
                    <Typography
                      sx={{
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: '0.32rem',
                        fontWeight: 600,
                        color: '#475569',
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.name.length > 8 ? `${item.name.slice(0, 8)}...` : item.name}
                    </Typography>
                  ) : (
                    <Box sx={{ width: 8, height: 1, bgcolor: '#d1d5db', borderRadius: 1 }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Legend */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        <Typography
          sx={{
            fontFamily: '"Montserrat", sans-serif',
            fontSize: '0.66rem',
            color: 'rgba(255,255,255,0.65)',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 0.3,
          }}
        >
          Legend
        </Typography>
        {[
          { color: colors.warmGold[500], label: 'Most Popular (Position #1)', style: 'solid' },
          { color: 'rgba(255,255,255,0.5)', label: 'Active in grid', style: 'solid' },
          { color: 'rgba(255,255,255,0.2)', label: 'Empty slot', style: 'dashed' },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '3px',
                border: `1.5px ${item.style} ${item.color}`,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Montserrat", sans-serif',
                fontSize: '0.66rem',
                color: 'rgba(255,255,255,0.65)',
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default AppMoodGridPanel;
